import {
  applyActionCode,
  checkActionCode,
  confirmPasswordReset,
  signOut,
  verifyPasswordResetCode
} from "firebase/auth";
import { LoaderCircle } from "lucide-react";
import {
  useEffect,
  useId,
  useRef,
  useState,
  type FC,
  type FormEvent,
  type ReactNode
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { AuthEmailAPI } from "@/api/authEmailApi";
import {
  AuthButton,
  AuthButtonLink,
  AuthCardFooter,
  AuthField,
  AuthFooterAction,
  AuthHeader,
  AuthShell,
  PasswordVisibilityToggle
} from "@/features/login";

import { auth } from "../../api/firebaseApi";
import { actionMode, oobCodeFromLink, signInContinueUrl } from "./actionUrl";

const MIN_PASSWORD = 8;

type View =
  | { kind: "working" }
  | { kind: "newPassword"; code: string; email: string; lead?: string }
  | { kind: "done"; title: string; body: string; resetPrompt?: boolean }
  | { kind: "failed"; title: string; body: string };

const EXPIRED: View = {
  kind: "failed",
  title: "This Link Has Expired",
  body: "It may have been used already. Request a new one and try again."
};

const ActionCard: FC<{
  title: string;
  subtitle?: ReactNode;
  children?: ReactNode;
}> = ({ title, subtitle, children }) => (
  <AuthShell>
    <div className="flex flex-col" data-testid="auth-action">
      <AuthHeader title={title} subtitle={subtitle} />
      {children ? <div className="mt-11 flex flex-col">{children}</div> : null}
    </div>
  </AuthShell>
);

const Email: FC<{ children: string }> = ({ children }) => (
  <span className="font-medium text-ink-soft">{children}</span>
);

type FieldError = { field: "password" | "confirm"; message: string };

const NewPasswordForm: FC<{
  onSubmit: (password: string) => Promise<void>;
}> = ({ onSubmit }) => {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [shown, setShown] = useState(false);
  const [error, setError] = useState<FieldError | null>(null);
  const [saving, setSaving] = useState(false);
  const id = useId();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (password.length < MIN_PASSWORD) {
      setError({
        field: "password",
        message: `Use at least ${MIN_PASSWORD} characters.`
      });
      return;
    }
    if (password !== confirm) {
      setError({ field: "confirm", message: "The passwords don't match." });
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await onSubmit(password);
    } catch (err) {
      const code = (err as { code?: string } | null)?.code;
      const weak =
        code === "auth/weak-password" ||
        code === "auth/password-does-not-meet-requirements";
      setError(
        weak
          ? { field: "password", message: "Choose a stronger password." }
          : {
              field: "confirm",
              message: "Couldn't save the password. The link may have expired."
            }
      );
    } finally {
      setSaving(false);
    }
  };

  const toggle = (
    <PasswordVisibilityToggle
      shown={shown}
      onToggle={() => setShown((value) => !value)}
    />
  );

  return (
    <form className="flex flex-col gap-5" noValidate onSubmit={submit}>
      <AuthField
        id={`${id}-password`}
        label="New Password"
        type={shown ? "text" : "password"}
        autoComplete="new-password"
        autoFocus
        value={password}
        onChange={(e) => {
          setPassword(e.currentTarget.value);
          if (error) setError(null);
        }}
        error={error?.field === "password" ? error.message : null}
        rightElement={toggle}
      />
      <AuthField
        id={`${id}-confirm`}
        label="Confirm Password"
        type={shown ? "text" : "password"}
        autoComplete="new-password"
        value={confirm}
        onChange={(e) => {
          setConfirm(e.currentTarget.value);
          if (error) setError(null);
        }}
        error={error?.field === "confirm" ? error.message : null}
        rightElement={toggle}
      />
      <AuthButton
        type="submit"
        fullWidth
        forward
        loading={saving}
        loadingText="Saving…"
      >
        Save Password
      </AuthButton>
    </form>
  );
};

/**
 * Firebase's custom action URL (`/auth/action`). Every account email link
 * lands here: password reset, email verification, second-factor and email
 * recovery notices, email sign-in (forwarded to its `continueUrl`), and the
 * backend's own email-change confirm and undo links.
 */
export const AuthActionPage: FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [view, setView] = useState<View>({ kind: "working" });
  const started = useRef(false);

  const mode = actionMode(params);
  const code = params.get("oobCode") ?? "";
  const token = params.get("token") ?? "";

  const showReset = async (oobCode: string, lead?: string) => {
    const email = await verifyPasswordResetCode(auth, oobCode);
    setView({ kind: "newPassword", code: oobCode, email, lead });
  };

  const run = async () => {
    const tenantId = params.get("tenantId");
    if (tenantId) auth.tenantId = tenantId;
    try {
      switch (mode) {
        case "signIn":
          navigate(signInContinueUrl(window.location.href), { replace: true });
          return;
        case "resetPassword":
          await showReset(code);
          return;
        case "verifyEmail":
          await applyActionCode(auth, code);
          await auth.currentUser?.reload();
          setView({
            kind: "done",
            title: "Email Verified",
            body: "Your email is verified. You can close this tab or go back to CaseBlink."
          });
          return;
        case "recoverEmail": {
          const info = await checkActionCode(auth, code);
          await applyActionCode(auth, code);
          setView({
            kind: "done",
            title: "Email Restored",
            body: `Your account email is back to ${
              info.data.email ?? "its previous address"
            }. If you didn't change it, reset your password now.`,
            resetPrompt: true
          });
          return;
        }
        case "revertSecondFactorAddition":
          await checkActionCode(auth, code);
          await applyActionCode(auth, code);
          setView({
            kind: "done",
            title: "Two-Step Sign-In Removed",
            body: "The phone number that was just added for two-step sign-in has been removed. If you didn't add it, reset your password now.",
            resetPrompt: true
          });
          return;
        case "confirmEmailChange": {
          const { email } = await AuthEmailAPI.confirmEmailChange(token);
          await signOut(auth).catch(() => undefined);
          setView({
            kind: "done",
            title: "Email Changed",
            body: `Your account email is now ${email}. Sign in again with your new email.`
          });
          return;
        }
        case "undoEmailChange": {
          const { email, reset_link } = await AuthEmailAPI.undoEmailChange(
            token
          );
          await signOut(auth).catch(() => undefined);
          const resetCode = oobCodeFromLink(reset_link);
          if (resetCode) {
            await showReset(
              resetCode,
              "Your email change was undone and every session was signed out. Choose a new password for"
            );
            return;
          }
          setView({
            kind: "done",
            title: "Email Change Undone",
            body: `Your account email is back to ${email}, and every session was signed out. Reset your password to secure your account.`,
            resetPrompt: true
          });
          return;
        }
        default:
          setView({
            kind: "failed",
            title: "This Link Isn't Valid",
            body: "Open the link from your email again, or request a new one."
          });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "";
      setView(
        message && (mode === "confirmEmailChange" || mode === "undoEmailChange")
          ? { kind: "failed", title: "This Link Didn't Work", body: message }
          : EXPIRED
      );
    }
  };

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void run();
  }, []);

  if (view.kind === "working") {
    return (
      <ActionCard
        title="One Moment"
        subtitle={
          <span className="inline-flex items-center gap-2">
            <LoaderCircle
              className="size-3.5 animate-spin text-ink-faint"
              aria-hidden="true"
            />
            Checking your link…
          </span>
        }
      />
    );
  }

  if (view.kind === "newPassword") {
    return (
      <ActionCard
        title="Choose a New Password"
        subtitle={
          view.lead ? (
            <>
              {view.lead} <Email>{view.email}</Email>.
            </>
          ) : (
            <>
              For <Email>{view.email}</Email>. Use at least {MIN_PASSWORD}{" "}
              characters.
            </>
          )
        }
      >
        <NewPasswordForm
          onSubmit={async (password) => {
            await confirmPasswordReset(auth, view.code, password);
            setView({
              kind: "done",
              title: "Password Saved",
              body: "Your new password is set. Sign in with it to continue."
            });
          }}
        />
      </ActionCard>
    );
  }

  const resetPrompt = view.kind === "done" && view.resetPrompt;
  const requestNewLink = view.kind === "failed" && mode === "resetPassword";
  return (
    <ActionCard title={view.title} subtitle={view.body}>
      {resetPrompt ? (
        <div className="flex flex-col gap-3">
          <AuthButtonLink to="/reset-password" fullWidth>
            Reset Password
          </AuthButtonLink>
          <AuthButtonLink to="/login" variant="secondary" fullWidth>
            Sign In
          </AuthButtonLink>
        </div>
      ) : (
        <AuthButtonLink to="/login" fullWidth>
          Sign In
        </AuthButtonLink>
      )}
      {requestNewLink ? (
        <AuthCardFooter>
          <AuthFooterAction to="/reset-password">
            Request a New Link
          </AuthFooterAction>
        </AuthCardFooter>
      ) : null}
    </ActionCard>
  );
};
